import { v4 as uuidv4 } from 'uuid'
import { LoginMethodType } from '@/pages/LoginMethod/types'

const LOGIN_TRACKING_KEY = 'login_tracking'
const MINUTES_TIMEOUT = 2
const MS_PER_SECOND = 1000
const SECONDS_PER_MINUTE = 60
const DEFAULT_TIMEOUT_MS = MINUTES_TIMEOUT * SECONDS_PER_MINUTE * MS_PER_SECOND

export interface LoginAttempt {
  method: LoginMethodType
  timestamp: number
  targetUrl: string
  attemptId: string
}

export interface LoginTrackingState {
  firstAttemptTimestamp: number
  attempts: LoginAttempt[]
}

/**
 * Store a new login attempt in session storage
 * Sets firstAttemptTimestamp if this is the first attempt
 * @param method The login method being attempted
 * @param targetUrl Where the user was trying to go
 * @returns The generated attemptId for event correlation
 */
export function storeLoginAttempt(
  method: LoginMethodType,
  targetUrl: string
): string {
  const attemptId = uuidv4()
  const now = Date.now()

  const existingState = getLoginTrackingState()
  const newAttempt: LoginAttempt = {
    method,
    timestamp: now,
    targetUrl,
    attemptId,
  }

  const newState: LoginTrackingState = {
    firstAttemptTimestamp: existingState?.firstAttemptTimestamp ?? now,
    attempts: [...(existingState?.attempts ?? []), newAttempt],
  }

  window.sessionStorage.setItem(LOGIN_TRACKING_KEY, JSON.stringify(newState))
  return attemptId
}

/**
 * Get the full login tracking state from session storage
 * @returns The tracking state or null if none exists
 */
export function getLoginTrackingState(): LoginTrackingState | null {
  const stored = window.sessionStorage.getItem(LOGIN_TRACKING_KEY)
  if (!stored) {
    return null
  }
  try {
    return JSON.parse(stored) as LoginTrackingState
  } catch {
    return null
  }
}

/**
 * Get the most recent login attempt
 * Used for loginSucceeded events
 */
export function getMostRecentAttempt(): LoginAttempt | null {
  const state = getLoginTrackingState()
  if (!state || state.attempts.length === 0) {
    return null
  }
  return state.attempts.at(-1) ?? null
}

/**
 * Get all methods tried in order (with duplicates)
 * Used for loginFailed event
 */
export function getAllMethodsTried(): LoginMethodType[] {
  const state = getLoginTrackingState()
  if (!state) {
    return []
  }
  return state.attempts.map((attempt) => attempt.method)
}

/**
 * Check if a login attempt is considered incomplete (i.e. failed).
 * A login is incomplete when enough time has passed since the first attempt
 * without a successful authentication. Since Auth0 handles credentials
 * externally, we cannot detect specific failure reasons (wrong password, etc.),
 * so we use a timeout heuristic: if the user returns to the login page after
 * the timeout period, we treat the login as incomplete/failed.
 * @param timeoutMs Timeout in milliseconds (default: 2 minutes)
 */
export function isLoginIncomplete(
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): boolean {
  const state = getLoginTrackingState()
  if (!state) {
    return false
  }
  return Date.now() - state.firstAttemptTimestamp >= timeoutMs
}

/**
 * Get elapsed time in milliseconds since the first attempt
 */
export function getElapsedMs(): number {
  const state = getLoginTrackingState()
  if (!state) {
    return 0
  }
  return Date.now() - state.firstAttemptTimestamp
}

/**
 * Check if there is any login tracking data
 */
export function hasLoginTrackingData(): boolean {
  return getLoginTrackingState() !== null
}

/**
 * Clear all login tracking data from session storage
 * Called on successful login
 */
export function clearAllLoginTracking(): void {
  window.sessionStorage.removeItem(LOGIN_TRACKING_KEY)
}
